// Epic fire eruption when Annie summons Tibbers.
// Phase 1 (0–300ms):  Dark summoning circle expands on the ground.
// Phase 2 (250–900ms): Fire erupts outward — two expanding rings + ember particles.
// Phase 3 (700–1200ms): Final flash fades, leaving Tibbers standing.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

interface Ember {
  angle: number;
  speed: number;
  size: number;
  startDelay: number;
  lifespan: number;
  color: string;
}

const TOTAL_MS = 1200;
const ERUPTION_START = 280;

export class TibbersSpawnVFX implements Animation {
  private elapsed = 0;
  private embers: Ember[];

  constructor(
    private center: Point,
    private hexSize: number,
  ) {
    this.embers = Array.from({ length: 28 }, (_, i) => {
      const colors = ["#ff6600", "#ff9900", "#ff3300", "#ffcc00"];
      return {
        angle: (i / 28) * Math.PI * 2 + (Math.random() - 0.5) * 0.6,
        speed: 55 + Math.random() * 110,
        size: 3 + Math.random() * 5,
        startDelay: ERUPTION_START + Math.random() * 220,
        lifespan: 380 + Math.random() * 420,
        color: colors[Math.floor(Math.random() * colors.length)],
      };
    });
  }

  update(dt: number): void {
    this.elapsed += dt;
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const { x, y } = this.center;
    const t = this.elapsed;

    ctx.save();

    // ── Phase 1: summoning circle on the ground (0 → ERUPTION_START + 200ms) ──
    if (t < ERUPTION_START + 200) {
      const p = Math.min(t / ERUPTION_START, 1);
      const r = this.hexSize * 1.0 * Easing.easeOutQuad(p);
      const fade = p < 1 ? 1 : 1 - (t - ERUPTION_START) / 200;

      // Dark ground shadow
      ctx.globalAlpha = fade * 0.55;
      ctx.fillStyle = "#1a0000";
      ctx.beginPath();
      ctx.ellipse(x, y, r, r * 0.35, 0, 0, Math.PI * 2);
      ctx.fill();

      // Summoning ring
      ctx.globalAlpha = fade * 0.9;
      ctx.strokeStyle = "#ff2200";
      ctx.lineWidth = 2.5;
      ctx.shadowColor = "#ff4400";
      ctx.shadowBlur = 18;
      ctx.beginPath();
      ctx.ellipse(x, y, r, r * 0.35, 0, 0, Math.PI * 2);
      ctx.stroke();

      // Inner rune glow
      ctx.globalAlpha = fade * 0.35;
      ctx.fillStyle = "#ff4400";
      ctx.beginPath();
      ctx.ellipse(x, y, r * 0.55, r * 0.18, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // ── Phase 2: fire eruption (ERUPTION_START → TOTAL_MS) ──
    if (t > ERUPTION_START) {
      const fireAge = t - ERUPTION_START;
      const fireDuration = TOTAL_MS - ERUPTION_START;
      const fp = Math.min(fireAge / fireDuration, 1);

      // Primary expanding ring
      const ringR = this.hexSize * 2.0 * Easing.easeOutCubic(fp);
      ctx.globalAlpha = (1 - fp) * 0.95;
      ctx.strokeStyle = "#ff6600";
      ctx.lineWidth = 5 * (1 - fp) + 2;
      ctx.shadowColor = "#ff3300";
      ctx.shadowBlur = 30;
      ctx.beginPath();
      ctx.arc(x, y, ringR, 0, Math.PI * 2);
      ctx.stroke();

      // Secondary ring (offset start)
      if (fireAge > 140) {
        const fp2 = Math.min((fireAge - 140) / (fireDuration - 140), 1);
        const ringR2 = this.hexSize * 1.3 * Easing.easeOutCubic(fp2);
        ctx.globalAlpha = (1 - fp2) * 0.75;
        ctx.strokeStyle = "#ffaa00";
        ctx.lineWidth = 3;
        ctx.shadowColor = "#ffcc00";
        ctx.shadowBlur = 18;
        ctx.beginPath();
        ctx.arc(x, y, ringR2, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Central burst flash
      const flashFade = Math.max(0, 1 - fp * 2.5);
      if (flashFade > 0) {
        const flashR = this.hexSize * 0.9 * (1 - Easing.easeInQuad(fp));
        ctx.globalAlpha = flashFade * 0.85;
        ctx.fillStyle = "#ffcc44";
        ctx.shadowColor = "#ff6600";
        ctx.shadowBlur = 40;
        ctx.beginPath();
        ctx.arc(x, y, flashR, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // ── Ember particles ──
    for (const e of this.embers) {
      const age = t - e.startDelay;
      if (age <= 0 || age > e.lifespan) continue;
      const ep = age / e.lifespan;
      const dist = e.speed * (age / 1000);
      const rise = age * 0.07;
      const ex = x + Math.cos(e.angle) * dist;
      const ey = y + Math.sin(e.angle) * dist - rise;
      const er = Math.max(0.5, e.size * (1 - ep * 0.65));

      ctx.globalAlpha = (1 - ep) * 0.95;
      ctx.fillStyle = e.color;
      ctx.shadowColor = e.color;
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.arc(ex, ey, er, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL_MS;
  }
}
